import { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { Plus, Trash2, Highlighter } from "lucide-react";
import { AutoTextarea } from "./shared";

const PALETTE = {
  sky: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-200",
  violet: "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200",
  rose: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200",
  amber: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200",
  orange: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200",
};

const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();

const AnnotatedTranscript = ({ transcript, labels, categories }) => {
  const parts = useMemo(() => {
    const src = transcript || "";
    const low = norm(src);
    const hits = [];
    labels.forEach((l, i) => {
      const q = norm(l.quote);
      if (q.length < 4) return;
      const idx = low.indexOf(q);
      if (idx >= 0) hits.push({ start: idx, end: idx + q.length, i });
    });
    hits.sort((a, b) => a.start - b.start);
    const out = [];
    let cur = 0;
    hits.forEach((h) => {
      if (h.start < cur) return;
      if (h.start > cur) out.push({ text: src.slice(cur, h.start) });
      out.push({ text: src.slice(h.start, h.end), i: h.i });
      cur = h.end;
    });
    if (cur < src.length) out.push({ text: src.slice(cur) });
    return out;
  }, [transcript, labels]);
  const colorOf = (i) => PALETTE[categories.find((c) => c.key === labels[i]?.category)?.color] || PALETTE.sky;
  return (
    <div className="rounded-xl border bg-muted/20 p-3 text-sm whitespace-pre-wrap leading-relaxed max-h-[420px] overflow-y-auto" data-testid="annotated-transcript">
      {parts.map((p, k) => (p.i == null ? <span key={k}>{p.text}</span> : <mark key={k} className={`rounded px-0.5 border ${colorOf(p.i)}`} title={`${labels[p.i].label} — ${labels[p.i].comment || ""}`}>{p.text}</mark>))}
    </div>
  );
};

export const LabelsForm = ({ data, onChange, specs, report, config }) => {
  const categories = specs[config.categories];
  const labels = data.labels || [];
  const [showTranscript, setShowTranscript] = useState(false);
  const [filter, setFilter] = useState("");
  const setLabel = (i, patch) => onChange({ ...data, labels: labels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const cat = (k) => categories.find((c) => c.key === k);
  const counts = categories.map((c) => ({ ...c, n: labels.filter((l) => l.category === c.key).length })).filter((c) => c.n);
  const visible = labels.map((l, i) => ({ l, i })).filter(({ l }) => !filter || l.category === filter);
  return (
    <div className="space-y-3" data-testid="labels-form">
      <div className="flex flex-wrap items-center gap-1.5">
        <button onClick={() => setFilter("")} className={`text-xs px-2 py-0.5 rounded-full border ${!filter ? "bg-foreground text-background" : "bg-background"}`} data-testid="labels-filter-all">Tout ({labels.length})</button>
        {counts.map((c) => <button key={c.key} onClick={() => setFilter(filter === c.key ? "" : c.key)} className={`text-xs px-2 py-0.5 rounded-full border ${PALETTE[c.color]} ${filter === c.key ? "ring-2 ring-offset-1 ring-foreground/40" : ""}`} data-testid={`labels-filter-${c.key}`}>{c.label} ({c.n})</button>)}
        {report?.transcript && <Button variant="ghost" size="sm" className="ml-auto gap-1.5" onClick={() => setShowTranscript(!showTranscript)} data-testid="labels-toggle-transcript"><Highlighter className="w-3.5 h-3.5" /> {showTranscript ? "Masquer" : "Transcription annotée"}</Button>}
      </div>
      {showTranscript && <AnnotatedTranscript transcript={report.transcript} labels={labels} categories={categories} />}
      {labels.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucune étiquette. Générez avec l'IA à partir de la transcription ou ajoutez-en manuellement.</p>}
      <div className="space-y-2">
        {visible.map(({ l, i }) => (
          <div key={i} className={`rounded-xl border-l-4 border p-3 grid md:grid-cols-[200px_1fr] gap-2 ${PALETTE[cat(l.category)?.color]?.split(" ").find((c) => c.startsWith("border-")) || ""}`} data-testid={`label-row-${i}`}>
            <div className="space-y-1.5">
              <select className="w-full text-xs rounded-lg border border-border bg-background px-2 py-1" value={l.category || ""} onChange={(e) => setLabel(i, { category: e.target.value })} data-testid={`label-category-${i}`}>
                <option value="">Catégorie…</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input className="w-full text-sm font-medium rounded-lg border border-border bg-background px-2 py-1" value={l.label || ""} onChange={(e) => setLabel(i, { label: e.target.value })} placeholder="Étiquette (ex. Dramatisation)" list={l.category === "distorsion_cognitive" ? "distortions-list" : undefined} data-testid={`label-name-${i}`} />
            </div>
            <div className="space-y-1.5 min-w-0">
              <div className="flex gap-2">
                <AutoTextarea value={l.quote} onChange={(v) => setLabel(i, { quote: v })} placeholder="Extrait de la transcription…" rows={2} className="italic" testId={`label-quote-${i}`} />
                <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => onChange({ ...data, labels: labels.filter((_, j) => j !== i) })} data-testid={`label-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <AutoTextarea value={l.comment} onChange={(v) => setLabel(i, { comment: v })} placeholder="Commentaire clinique…" rows={1} className="text-xs" testId={`label-comment-${i}`} />
            </div>
          </div>
        ))}
      </div>
      <datalist id="distortions-list">{specs.distortions.map((d) => <option key={d} value={d} />)}</datalist>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onChange({ ...data, labels: [...labels, { quote: "", category: categories[0].key, label: "", comment: "" }] })} data-testid="label-add"><Plus className="w-3.5 h-3.5" /> Ajouter une étiquette</Button>
    </div>
  );
};
